import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';

import type { Editor } from '@tiptap/core';
import { RichTextEditorInput } from './RichTextEditorInput';
import { EditorDropzone } from './components/EditorDropzone';
import { Placeholder } from './components/Placeholder';
import { RichTextEditorProvider } from './components/RichTextEditorProvider';
import { useFileUpload } from './hooks/useFileUpload';
import { useImageProgressEffect } from './hooks/useImageProgressEffect';
import './tiptapNodeViewPatch';
import { useRichTextEditor } from './useRichTextEditor';
import { isEditorPristine } from './utils';
import { cn } from '@simplywallst/ui-core/next';
import { InlineToolbar } from './components/InlineToolbar';

export type Content = {
  html: string;
  text: string;
};

interface Props {
  defaultContent?: string;
  placeholderText?: ReactNode;
  characterLimit?: number;
  enableImages?: boolean;
  onUpdate?: (content: Content) => void;
  onInit?: (content: Content, editor: Editor) => void;
  onError?: (error: string) => void;
  autoFocus?: boolean;
  children?: ReactNode;
  className?: string;
}

export const RichTextEditor = ({
  defaultContent,
  placeholderText,
  characterLimit,
  enableImages = false,
  onUpdate,
  onInit,
  onError,
  autoFocus,
  children,
  className,
}: Props) => {
  const isTextualPlaceholder =
    typeof placeholderText === 'string' ||
    typeof placeholderText === 'undefined';

  const { handleFileUpload, progress, urlRef, error } = useFileUpload();
  const {
    editor,
    isActive,
    format,
    focus,
    getCharacterCount,
    resetEditorContent,
  } = useRichTextEditor({
    defaultContent,
    placeholderText: isTextualPlaceholder ? placeholderText : null,
    characterLimit,
    onUpdate: (editor) => {
      onUpdate?.({
        text: editor.getText(),
        html: editor.getHTML(),
      });
    },
    onInit: (editor) => {
      onInit?.(
        {
          text: editor.getText(),
          html: editor.getHTML(),
        },
        editor
      );
    },
    onError,
    handleFileUpload: enableImages ? handleFileUpload : undefined,
  });

  useEffect(() => {
    queueMicrotask(() => {
      resetEditorContent();
    });
  }, [defaultContent, resetEditorContent]);

  useEffect(() => {
    if (autoFocus) {
      focus('end');
    }
  }, [focus, autoFocus]);

  const focusEditor = useCallback(() => {
    if (getCharacterCount() === 0) {
      focus('end');
    }
  }, [getCharacterCount, focus]);

  useImageProgressEffect({ editor, progress, urlRef, error });

  return (
    <RichTextEditorProvider
      isActive={isActive}
      format={format}
      editor={editor}
      enableImages={enableImages}
      handleFileUpload={handleFileUpload}
      characterLimit={characterLimit}
    >
      <EditorDropzone
        pristine={!!isEditorPristine(editor)}
        enableImages={enableImages}
        onFileDrop={handleFileUpload}
        onClick={focusEditor}
        className={cn(
          'grid h-full grid-cols-1 grid-rows-[1fr_max-content]',
          className
        )}
      >
        {editor && (
          <div className="scrollbar h-full overflow-y-auto overflow-x-hidden">
            <RichTextEditorInput editor={editor} />
            {!isTextualPlaceholder && placeholderText && (
              <Placeholder editor={editor} placeholder={placeholderText} />
            )}
          </div>
        )}
        <InlineToolbar />
        {children}
      </EditorDropzone>
    </RichTextEditorProvider>
  );
};
